<template>
  <div>
    <!-- Cabeçalho -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h5 class="mb-0">E-mail Marketing - Responsáveis Ativos</h5>
      <b-button variant="primary" size="sm" @click="copyEmails">
        <i class="bi bi-clipboard"></i> Copiar E-mails
      </b-button>
    </div>

    <!-- Filtros -->
    <div class="d-flex mb-3" style="gap:10px;">
      <input
        v-model="filtroResponsavel"
        placeholder="Filtrar por responsável"
        class="form-control"
        @input="onFilterChange"
      />
      <input
        v-model="filtroAluno"
        placeholder="Filtrar por aluno"
        class="form-control"
        @input="onFilterChange"
      />
      <input
        v-model="filtroEmail"
        placeholder="Filtrar por e-mail"
        class="form-control"
        @input="onFilterChange"
      />
      <b-form-select
        v-model="filtroNivel"
        :options="nivelOptions"
        class="form-control"
        @change="onFilterChange"
      />
      <b-button variant="secondary" @click="resetFilters">
        <i class="bi bi-funnel"></i>
      </b-button>
    </div>

    <!-- Tabela -->
    <table class="table table-striped table-hover">
      <thead>
        <tr>
          <th>
            <button class="btn btn-link p-0" @click="onSort('responsavel')">
              Responsável {{ arrow('responsavel') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="onSort('aluno')">
              Aluno {{ arrow('aluno') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="onSort('nivel')">
              Nível {{ arrow('nivel') }}
            </button>
          </th>
          <th>
            <button class="btn btn-link p-0" @click="onSort('email')">
              E-mail {{ arrow('email') }}
            </button>
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="row in items" :key="row.id">
          <td>{{ row.responsavel }}</td>
          <td>{{ row.aluno }}</td>
          <td>{{ row.nivel }}</td>
          <td>{{ row.email }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Paginação -->
    <div class="d-flex justify-content-between align-items-center mt-3">
      <div>
        Mostrando {{ startRow }} a {{ endRow }} de {{ totalItems }} registros
      </div>
      <div class="d-flex align-items-center" style="gap:.5rem;">
        <b-form-select
          v-model.number="limit"
          :options="pageOptions"
          size="sm"
          style="width:6rem"
        />
      </div>
      <div>
        <b-pagination
          v-model="page"
          :per-page="limit"
          :total-rows="totalItems"
          size="sm"
          limit="5"
        />
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue';
import axios from '@/services/axios';
import { BFormSelect, BPagination, BButton } from 'bootstrap-vue-next';

// dados da tabela
const items      = ref([]);
const totalItems = ref(0);

// página/ordenação
const page      = ref(1);
const limit     = ref(10);
const sortField = ref('responsavel');
const sortOrder = ref('asc');

// filtros
const filtroResponsavel = ref('');
const filtroAluno       = ref('');
const filtroNivel       = ref('');
const filtroEmail = ref('');


// opções de níveis
const niveis = ref([]);
const nivelOptions = computed(() => [
  { value: '', text: 'Todos os Níveis' },
  ...niveis.value.map(n => ({ value: String(n.id), text: n.nivel }))
]);

// paginação
const pageOptions = [10, 15, 20, 25, 50];
const startRow    = computed(() => (page.value - 1) * limit.value + 1);
const endRow      = computed(() => Math.min(page.value * limit.value, totalItems.value));


// seta ícone de ordenação
function arrow(col) {
  if (sortField.value !== col) return '↕';
  return sortOrder.value === 'asc' ? '↑' : '↓';
}

// carga geral
async function loadData(override = {}) {
  const params = {
    page:      page.value,
    limit:     limit.value,
    sortField: sortField.value,
    sortOrder: sortOrder.value,
    ...override
  };
  if (filtroResponsavel.value) params.responsavel = filtroResponsavel.value;
  if (filtroAluno.value)       params.aluno       = filtroAluno.value;
  if (filtroNivel.value)       params.nivel       = parseInt(filtroNivel.value, 10);
  if (filtroEmail.value)       params.email       = filtroEmail.value;

  const res = await axios.get('/operacional/emarketing-respativos', { params });
  items.value      = res.data.data;
  totalItems.value = res.data.total;
}

// handlers
function onFilterChange() {
  page.value = 1;
  loadData();
}
function resetFilters() {
  filtroResponsavel.value = '';
  filtroAluno.value       = '';
  filtroNivel.value       = '';
  filtroEmail.value       = '';
  page.value              = 1;
  loadData();
}
function onSort(col) {
  if (sortField.value === col) {
    sortOrder.value = sortOrder.value === 'asc' ? 'desc' : 'asc';
  } else {
    sortField.value = col;
    sortOrder.value = 'asc';
  }
  page.value = 1;
  loadData();
}

// reativa carga quando página ou limite mudam
watch([page, limit], () => loadData());

// montagem inicial
onMounted(async () => {
  try {
    const res = await axios.get('/cadastros/niveis-turmas', {
      params: { ativo: true, page: 1, limit: 100 }
    });
    niveis.value = Array.isArray(res.data.data) ? res.data.data : res.data;
  } catch (e) {
    console.error('Erro ao carregar níveis:', e);
  }
  loadData();
});

// copiar e-mails com fallback para contextos sem Clipboard API
async function copyEmails() {
  // busca todos os registros
  const override = {
    page:  1,
    limit: totalItems.value,
    sortField: sortField.value,
    sortOrder: sortOrder.value
  };
  if (filtroResponsavel.value) override.responsavel = filtroResponsavel.value;
  if (filtroAluno.value)       override.aluno       = filtroAluno.value;
  if (filtroNivel.value)       override.nivel       = parseInt(filtroNivel.value, 10);
  if (filtroEmail.value)       override.email       = filtroEmail.value;

  const res = await axios.get('/operacional/emarketing-respativos', { params: override });
  const text = res.data.data.map(r => r.email).filter(Boolean).join(', ');

  if (navigator.clipboard?.writeText) {
    // API moderna
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Falha no clipboard.writeText:', err);
    }
  } else {
    // fallback: textarea + execCommand
    const textarea = document.createElement('textarea');
    textarea.value = text;
    textarea.setAttribute('readonly', '');
    textarea.style.position = 'absolute';
    textarea.style.left = '-9999px';
    document.body.appendChild(textarea);
    textarea.select();
    try {
      document.execCommand('copy');
    } catch (err) {
      console.error('Fallback: não foi possível copiar', err);
    }
    document.body.removeChild(textarea);
  }

  alert(`Copiados ${res.data.data.length} e-mails de Responsáveis Ativos.`);
}
</script>

<style scoped>
:deep(.pagination-sm .page-link) {
  font-size: 0.8rem;
  line-height: 1;
  border-radius: 0;
}
</style>
